import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { addRound, updateRound, deleteRound } from '@/store/tournamentSlice';
import { Round } from '@/types';
import { formatDuration } from '@/utils/timeUtils';
import clsx from 'clsx';
import { Timer, Plus, Trash2, Edit3, Copy, Download, Upload } from 'lucide-react';

export const RoundsManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tournament = useSelector((state: RootState) => state.tournament.current);
  const currentRound = useSelector((state: RootState) => state.clock.currentRound);
  
  const [editingRound, setEditingRound] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Round>>({});
  const [showAddForm, setShowAddForm] = useState(false);

  const rounds = tournament?.rounds || [];

  const defaultRound: Omit<Round, 'id'> = {
    minutes: 15,
    smallBlind: 100,
    bigBlind: 200,
    ante: 0,
    limit1: 0,
    limit2: 0,
    breakMinutes: 0,
  };

  const handleAddRound = () => {
    const newRound: Round = {
      ...defaultRound,
      id: crypto.randomUUID(),
    };
    dispatch(addRound(newRound));
    setShowAddForm(false);
  };

  const handleStartEdit = (index: number) => {
    setEditingRound(index);
    setEditForm(rounds[index]);
  };

  const handleSaveEdit = () => {
    if (editingRound !== null && editForm.minutes && editForm.smallBlind && editForm.bigBlind) {
      dispatch(updateRound({
        index: editingRound,
        round: editForm as Round
      }));
      setEditingRound(null);
      setEditForm({});
    }
  };

  const handleCancelEdit = () => {
    setEditingRound(null);
    setEditForm({});
  };

  const handleDeleteRound = (index: number) => {
    if (confirm('Are you sure you want to delete this round?')) {
      dispatch(deleteRound(index));
    }
  };

  const handleDuplicateRound = (index: number) => {
    const roundToDuplicate = rounds[index];
    const newRound: Round = {
      ...roundToDuplicate,
      id: crypto.randomUUID(),
    };
    dispatch(addRound(newRound));
  };

  const generateStandardStructure = () => {
    const standardRounds: Round[] = [
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 25, bigBlind: 50, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 50, bigBlind: 100, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 75, bigBlind: 150, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 100, bigBlind: 200, ante: 0, breakMinutes: 5 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 200, bigBlind: 400, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 300, bigBlind: 600, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 400, bigBlind: 800, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 500, bigBlind: 1000, ante: 0, breakMinutes: 5 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 1000, bigBlind: 2000, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 1500, bigBlind: 3000, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 2000, bigBlind: 4000, ante: 0, breakMinutes: 0 },
      { id: crypto.randomUUID(), minutes: 15, smallBlind: 3000, bigBlind: 6000, ante: 0, breakMinutes: 5 },
    ];

    // Replace current rounds with standard structure
    tournament?.rounds.forEach((_, index) => {
      dispatch(deleteRound(0)); // Always delete from index 0 as array shifts
    });

    standardRounds.forEach(round => {
      dispatch(addRound(round));
    });
  };

  const calculateTournamentDuration = () => {
    const totalMinutes = rounds.reduce((sum, round) => sum + round.minutes + round.breakMinutes, 0);
    return formatDuration(totalMinutes * 60);
  };

  const formatCurrency = (amount: number) => {
    return `${tournament?.settings.currencySymbol || '$'}${amount.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Timer size={24} />
              Round Structure ({rounds.length} rounds)
            </h2>
            
            <div className="flex gap-2">
              <button
                onClick={generateStandardStructure}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <Timer size={16} />
                Standard Structure
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                <Plus size={16} />
                Add Round
              </button>
            </div>
          </div>
          
          {rounds.length > 0 && (
            <div className="mt-3 text-sm text-gray-600">
              Total estimated duration: {calculateTournamentDuration()}
            </div>
          )}
        </div>

        {/* Add Round Form */}
        {showAddForm && (
          <div className="p-6 border-b border-gray-200 bg-blue-50">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Round</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <button
              onClick={handleAddRound}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              Add Round with Default Values
            </button>
          </div>
        )}

        {/* Rounds Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Round
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Small Blind
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Big Blind
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ante
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Break
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rounds.map((round, index) => (
                <tr
                  key={round.id}
                  className={clsx(
                    'hover:bg-gray-50 transition-colors',
                    {
                      'bg-blue-50 border-blue-200': index + 1 === currentRound,
                      'bg-green-50': editingRound === index,
                    }
                  )}
                >
                  {/* Round Number */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={clsx(
                        'font-medium',
                        {
                          'text-blue-600': index + 1 === currentRound,
                          'text-gray-900': index + 1 !== currentRound,
                        }
                      )}>
                        {index + 1}
                      </span>
                      {index + 1 === currentRound && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Duration */}
                  <td className="px-4 py-3">
                    {editingRound === index ? (
                      <input
                        type="number"
                        value={editForm.minutes || ''}
                        onChange={(e) => setEditForm({ ...editForm, minutes: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                        min="1"
                        max="120"
                      />
                    ) : (
                      <span className="text-gray-900">{round.minutes}m</span>
                    )}
                  </td>

                  {/* Small Blind */}
                  <td className="px-4 py-3">
                    {editingRound === index ? (
                      <input
                        type="number"
                        value={editForm.smallBlind || ''}
                        onChange={(e) => setEditForm({ ...editForm, smallBlind: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-24"
                        min="0"
                      />
                    ) : (
                      <span className="text-gray-900 font-medium">{formatCurrency(round.smallBlind)}</span>
                    )}
                  </td>

                  {/* Big Blind */}
                  <td className="px-4 py-3">
                    {editingRound === index ? (
                      <input
                        type="number"
                        value={editForm.bigBlind || ''}
                        onChange={(e) => setEditForm({ ...editForm, bigBlind: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-24"
                        min="0"
                      />
                    ) : (
                      <span className="text-gray-900 font-medium">{formatCurrency(round.bigBlind)}</span>
                    )}
                  </td>

                  {/* Ante */}
                  <td className="px-4 py-3">
                    {editingRound === index ? (
                      <input
                        type="number"
                        value={editForm.ante || ''}
                        onChange={(e) => setEditForm({ ...editForm, ante: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-24"
                        min="0"
                      />
                    ) : (
                      <span className="text-gray-600">
                        {round.ante > 0 ? formatCurrency(round.ante) : '-'}
                      </span>
                    )}
                  </td>

                  {/* Break */}
                  <td className="px-4 py-3">
                    {editingRound === index ? (
                      <input
                        type="number"
                        value={editForm.breakMinutes || ''}
                        onChange={(e) => setEditForm({ ...editForm, breakMinutes: parseInt(e.target.value) || 0 })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                        min="0"
                        max="60"
                      />
                    ) : (
                      <span className="text-gray-600">
                        {round.breakMinutes > 0 ? `${round.breakMinutes}m` : '-'}
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {editingRound === index ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            className="text-green-600 hover:text-green-800 px-1"
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-red-600 hover:text-red-800 px-1"
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStartEdit(index)}
                            className="text-blue-600 hover:text-blue-800 px-1"
                            title="Edit"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={() => handleDuplicateRound(index)}
                            className="text-purple-600 hover:text-purple-800 px-1"
                            title="Duplicate"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteRound(index)}
                            className="text-red-600 hover:text-red-800 px-1"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {rounds.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Timer size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg mb-2">No rounds configured</p>
              <p className="text-sm">Add rounds to define your tournament structure</p>
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use the "Standard Structure" button to generate a common tournament structure</li>
          <li>• Add breaks between rounds to give players time to rest</li>
          <li>• Gradually increase blinds to maintain tournament pace</li>
          <li>• The current round is highlighted in blue</li>
        </ul>
      </div>
    </div>
  );
};